import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";

import { BrowserView, MobileView } from "react-device-detect";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import dayjs from "dayjs";

import ProgressBar from "../../components/ProgressBar";
import { getStudentScores } from "../../services/ScoreService";
import { hasError, getError } from "../../services/ServiceHelper";

import { generateUUID } from "../../services/AbstractService";
import {
  StripedTableCell,
  StripedTableHeadCell,
  StripedTableRow,
} from "../../components/StripedTable";
import TableRowNoResults from "../../components/TableRowNoResults";

const StudentScores = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_showProgress, setShowProgress] = useState(false);

  const [_totalPoints, setTotalPoints] = useState(0);
  const [_totalScores, setTotalScores] = useState(0);

  const [_scores, setScores] = useState([]);
  const [, setHouseScores] = useState([]);
  const [, setUserListScores] = useState([]);
  const [, setTeamScores] = useState([]);

  const initScores = () => {
    setShowProgress(true);
    getStudentScores(_school, _jwt)
      .then((results) => {
        if (hasError(results) === true) {
          window.showSnack(
            "Unable to get Student Scores: " + getError(results)
          );
          return;
        }

        setScores(results.data.scores);
        setHouseScores(results.data.house_scores);
        setUserListScores(results.data.user_list_scores);
        setTeamScores(results.data.team_scores);

        let totalPoints = 0;
        for (let score of results.data.scores) {
          totalPoints += score.scoreValue;
        }

        setTotalPoints(totalPoints);
        setTotalScores(results.data.scores.length);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  useEffect(() => {
    initScores();
  }, []);

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Student Score/Tardy History" showBack={true} />
        <ProgressBar showProgress={_showProgress} />

        <Accordion disableGutters sx={{ boxShadow: 1, p: 0, mt: 2 }} expanded>
          <AccordionSummary className="accordionTitle">
            Score Totals
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table sx={{ minWidth: 320 }} aria-label="score totals table">
                <TableBody>
                  <StripedTableRow key="trTotalScores">
                    <StripedTableHeadCell component="th" scope="row">
                      Total Scores:
                    </StripedTableHeadCell>
                    <StripedTableCell>{_totalScores}</StripedTableCell>
                  </StripedTableRow>
                  <TableRowNoResults data={_scores} />
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ boxShadow: 1, p: 0, mt: 2 }} expanded>
          <AccordionSummary className="accordionTitle">
            Scores / Tardies
          </AccordionSummary>
          <AccordionDetails>
            <BrowserView>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table sx={{ minWidth: 400 }} aria-label="score totals table">
                  <TableHead>
                    <StripedTableRow key="trGeneralHeader">
                      <StripedTableCell>Date</StripedTableCell>
                      <StripedTableCell>Type</StripedTableCell>
                      <StripedTableCell>Comment</StripedTableCell>
                      <StripedTableCell>Consequence</StripedTableCell>
                    </StripedTableRow>
                  </TableHead>
                  <TableBody>
                    {_scores.map((score) => (
                      <StripedTableRow
                        key={generateUUID()}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <StripedTableCell component="th" scope="row">
                          {dayjs(score.scoreDate).format("MM/DD/YY")}{" "}
                          - {score.last_name}
                        </StripedTableCell>
                        <StripedTableCell>
                          {score.categoryName}
                        </StripedTableCell>
                        <StripedTableCell>
                          {score.comment != null && score.comment.length > 0
                            ? score.comment
                            : "NONE"}
                        </StripedTableCell>
                        <StripedTableCell>
                          {score.conName != null && score.conName.length > 0
                            ? score.conName
                            : "NONE"}
                        </StripedTableCell>
                      </StripedTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </BrowserView>

            <MobileView>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table sx={{ minWidth: 400 }} aria-label="score totals table">
                  <TableHead>
                    <StripedTableRow key="trGeneralDetails">
                      <StripedTableCell>Details</StripedTableCell>
                    </StripedTableRow>
                  </TableHead>
                  <TableBody>
                    {_scores.map((score) => (
                      <StripedTableRow
                        key={generateUUID()}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <StripedTableCell component="th" scope="row">
                          DATE: {score.scoreDate} - {score.last_name} <br />
                          TYPE: {score.categoryName} <br />
                          POINTS: {score.scoreValue} <br />
                          {score.comment != null &&
                            score.comment.length > 0 && (
                              <>
                                COMMENT: {score.comment} <br />
                              </>
                            )}
                          {score.conName != null &&
                            score.conName.length > 0 && (
                              <>
                                CONSEQUENCE: {score.conName} <br />
                              </>
                            )}
                        </StripedTableCell>
                      </StripedTableRow>
                    ))}
                    <TableRowNoResults data={_scores} />
                  </TableBody>
                </Table>
              </TableContainer>
            </MobileView>
          </AccordionDetails>
        </Accordion>

        {/* <Box sx={{ boxShadow: 1, p: 1, mt: 1 }}>
          <SubTitle text="Den/House Scores" />

          <BrowserView>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table
                sx={{ minWidth: 400 }}
                aria-label="den/house score totals table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Consequence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {houseScores.map((score) => (
                    <TableRow
                      key={generateUUID()}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        <Moment date={score.scoreDate} format="MM/DD/YYYY" /> - {score.last_name}
                      </TableCell>
                      <TableCell>{score.categoryName}</TableCell>
                      <TableCell>{score.scoreValue}</TableCell>
                      <TableCell>
                        {score.comment != null && score.comment.length > 0
                          ? score.comment
                          : "NONE"}
                      </TableCell>
                      <TableCell>
                        {score.conName != null && score.conName.length > 0
                          ? score.conName
                          : "NONE"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {houseScores.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        -- NO RESULTS RETURNED --
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </BrowserView>

          <MobileView>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table sx={{ minWidth: 400 }} aria-label="score totals table">
                <TableHead>
                  <TableRow>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {houseScores.map((score) => (
                    <TableRow
                      key={generateUUID()}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        DATE: {score.scoreDate} - {score.last_name} <br />
                        TYPE: {score.categoryName} <br />
                        POINTS: {score.scoreValue} <br />
                        {score.comment != null && score.comment.length > 0 && (
                          <>
                            COMMENT: {score.comment} <br />
                          </>
                        )}
                        {score.conName != null && score.conName.length > 0 && (
                          <>
                            CONSEQUENCE: {score.conName} <br />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {houseScores.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        -- NO RESULTS RETURNED --
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MobileView>
        </Box> */}

        {/* <Box sx={{ boxShadow: 1, p: 1, mt: 1 }}>
          <SubTitle text="User List Scores" />

          <BrowserView>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table
                sx={{ minWidth: 400 }}
                aria-label="user list score totals table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Consequence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userListScores.map((score) => (
                    <TableRow
                      key={generateUUID()}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        <Moment date={score.scoreDate} format="MM/DD/YYYY" /> - {score.last_name}
                      </TableCell>
                      <TableCell>{score.categoryName}</TableCell>
                      <TableCell>{score.scoreValue}</TableCell>
                      <TableCell>
                        {score.comment != null && score.comment.length > 0
                          ? score.comment
                          : "NONE"}
                      </TableCell>
                      <TableCell>
                        {score.conName != null && score.conName.length > 0
                          ? score.conName
                          : "NONE"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {userListScores.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        -- NO RESULTS RETURNED --
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </BrowserView>

          <MobileView>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table sx={{ minWidth: 400 }} aria-label="score totals table">
                <TableHead>
                  <TableRow>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userListScores.map((score) => (
                    <TableRow
                      key={generateUUID()}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        DATE: {score.scoreDate} - {score.last_name} <br />
                        TYPE: {score.categoryName} <br />
                        POINTS: {score.scoreValue} <br />
                        {score.comment != null && score.comment.length > 0 && (
                          <>
                            COMMENT: {score.comment} <br />
                          </>
                        )}
                        {score.conName != null && score.conName.length > 0 && (
                          <>
                            CONSEQUENCE: {score.conName} <br />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {houseScores.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        -- NO RESULTS RETURNED --
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </MobileView>
        </Box> */}
      </Container>
    </>
  );
};

export default StudentScores;
